import React, { useState } from 'react';
import { router, Link } from '@inertiajs/react';
import { cn } from '../../../design-system/utils/cn';
import { AdminLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  DataTable,
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Checkbox,
  StatCard,
  Tabs,
  TabPanel,
  ConfirmDialog,
  Modal,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '../../../design-system/components';
import { formatCurrency, formatDateTime } from '../../../lib/formatters';
import {
  admin_payouts_path,
  export_csv_admin_payouts_path,
  bulk_mark_paid_admin_payouts_path,
  paypal_payout_admin_payouts_path,
  admin_settings_paypal_settings_path,
  admin_affiliate_path,
  admin_commissions_path,
} from '../../../lib/routes';
import { AlertTriangle, Download, Wallet, ChevronDown } from 'lucide-react';

const BATCH_STATUS_VARIANTS = {
  pending: 'warning',
  processing: 'info',
  success: 'success',
  failed: 'danger',
};

export default function Index({ payout_entries, tab, stats, pagination, paypal_configured, recent_batches }) {
  const [expandedBatches, setExpandedBatches] = useState(new Set());
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPaypalConfirm, setShowPaypalConfirm] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [skipMinimum, setSkipMinimum] = useState(false);

  const selectedCount = selectedRows.size;
  const selectedEntries = payout_entries.filter((e) => selectedRows.has(e.affiliate_id));
  const selectedTotal = selectedEntries.reduce((sum, e) => sum + e.total_owed_cents, 0);

  const columns = [
    {
      id: 'name',
      header: 'Affiliate',
      render: (row) => (
        <Link href={admin_affiliate_path(row.affiliate_id)} className="block hover:underline">
          <div className="font-medium text-fg">{row.name}</div>
          <div className="text-xs text-fg-tertiary">{row.email}</div>
        </Link>
      ),
    },
    {
      id: 'paypal_email',
      header: 'PayPal Email',
      render: (row) =>
        row.missing_paypal_email ? (
          <Badge variant="warning" size="sm">
            <AlertTriangle size={12} className="mr-1" />
            Missing
          </Badge>
        ) : (
          <span className="text-sm text-fg-secondary">{row.paypal_email}</span>
        ),
    },
    {
      id: 'commission_count',
      header: 'Commissions',
      render: (row) => (
        <Link
          href={`${admin_commissions_path()}?affiliate_id=${row.affiliate_id}&status=approved`}
          className="text-sm text-fg-secondary hover:text-fg hover:underline"
        >
          {row.commission_count}
        </Link>
      ),
    },
    {
      id: 'total_owed_cents',
      header: 'Amount Owed',
      render: (row) => (
        <span className="font-medium">{formatCurrency(row.total_owed_cents)}</span>
      ),
    },
  ];

  // Upcoming tab gets a progress column instead of status
  const upcomingColumns = [
    ...columns,
    {
      id: 'progress',
      header: 'Progress',
      render: (row) => (
        <span className="text-sm text-fg-tertiary">
          {formatCurrency(row.total_owed_cents)} / {formatCurrency(row.min_payout_cents)}
        </span>
      ),
    },
  ];

  function handleTabChange(newTab) {
    setSelectedRows(new Set());
    router.visit(admin_payouts_path(), {
      data: { tab: newTab },
      preserveState: false,
    });
  }

  function handleExportCsv() {
    const params = new URLSearchParams();
    if (selectedCount > 0) {
      Array.from(selectedRows).forEach((id) => params.append('affiliate_ids[]', id));
    }
    window.open(`${export_csv_admin_payouts_path()}?${params.toString()}`, '_blank');
  }

  function handleBulkMarkPaid() {
    setBulkLoading(true);
    router.post(
      bulk_mark_paid_admin_payouts_path(),
      { affiliate_ids: Array.from(selectedRows) },
      {
        onFinish: () => {
          setBulkLoading(false);
          setShowConfirm(false);
          setSelectedRows(new Set());
        },
      },
    );
  }

  function handlePageChange(page) {
    router.visit(admin_payouts_path(), {
      data: {
        tab,
        page: String(page),
      },
      preserveState: true,
      preserveScroll: true,
      replace: true,
    });
  }

  function handlePaypalPayout() {
    setPaypalLoading(true);
    const data = { affiliate_ids: Array.from(selectedRows) };
    if (skipMinimum) data.skip_minimum = 'true';
    router.post(
      paypal_payout_admin_payouts_path(),
      data,
      {
        onFinish: () => {
          setPaypalLoading(false);
          setShowPaypalConfirm(false);
          setSkipMinimum(false);
          setSelectedRows(new Set());
        },
      },
    );
  }

  const bulkActionBar = (
    <div className="flex items-center gap-3">
      <Button size="sm" variant="outline" onClick={handleExportCsv}>
        <Download size={16} className="mr-1.5" />
        Export CSV ({selectedCount})
      </Button>
      {paypal_configured && (
        <Button size="sm" onClick={() => setShowPaypalConfirm(true)}>
          <Wallet size={16} className="mr-1.5" />
          Pay ({selectedCount})
        </Button>
      )}
      <Button size="sm" variant={paypal_configured ? 'outline' : 'default'} onClick={() => setShowConfirm(true)}>
        Mark as Paid ({selectedCount})
      </Button>
      <span className="text-sm text-fg-secondary">Total: {formatCurrency(selectedTotal)}</span>
    </div>
  );

  return (
    <AdminLayout>
      <PageHeader title="Payouts" description="Review and process affiliate payouts" />

      {!paypal_configured && (
        <Alert variant="warning" title="PayPal not configured" className="mt-4">
          Configure PayPal API credentials in{' '}
          <a href={admin_settings_paypal_settings_path()} className="underline font-medium">
            PayPal Settings
          </a>{' '}
          to enable direct payouts.
        </Alert>
      )}

      <Tabs
        tabs={[
          { id: 'due', label: 'Due', count: stats.due_count },
          { id: 'upcoming', label: 'Upcoming', count: stats.upcoming_count },
          { id: 'history', label: 'History', count: recent_batches.length },
        ]}
        activeTab={tab}
        onChange={handleTabChange}
        className="mt-6"
      />

      <TabPanel tabId="due" activeTab={tab}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <StatCard label="Affiliates Due" value={stats.due_count} />
          <StatCard label="Total Due" value={formatCurrency(stats.due_total_cents)} />
        </div>

        <div className="mt-6 flex items-center justify-end">
          <Button size="sm" variant="outline" onClick={handleExportCsv}>
            <Download size={16} className="mr-1.5" />
            {selectedCount > 0 ? `Export Selected (${selectedCount})` : 'Export All'}
          </Button>
        </div>

        <div className="mt-4">
          <DataTable
            columns={columns}
            data={payout_entries}
            keyAccessor="affiliate_id"
            selectable
            selectedRows={selectedRows}
            onSelectionChange={setSelectedRows}
            isRowSelectable={(row) => !row.missing_paypal_email}
            disabledRowTooltip={() => 'Missing payment info'}
            bulkActions={bulkActionBar}
            pagination={{
              page: pagination.current_page,
              totalPages: pagination.total_pages,
              onPageChange: handlePageChange,
            }}
            emptyTitle="No payouts due"
            emptyDescription="Affiliates meeting the minimum payout threshold will appear here."
          />
        </div>
      </TabPanel>

      <TabPanel tabId="upcoming" activeTab={tab}>
        <div className="mt-4">
          <DataTable
            columns={upcomingColumns}
            data={payout_entries}
            keyAccessor="affiliate_id"
            pagination={{
              page: pagination.current_page,
              totalPages: pagination.total_pages,
              onPageChange: handlePageChange,
            }}
            emptyTitle="No upcoming payouts"
            emptyDescription="Affiliates below the minimum payout threshold will appear here."
          />
        </div>
      </TabPanel>

      <TabPanel tabId="history" activeTab={tab}>
        {recent_batches.length === 0 ? (
          <Card>
            <CardBody>
              <div className="text-center py-8">
                <Wallet size={32} className="mx-auto text-fg-tertiary mb-2" />
                <p className="text-sm text-fg-secondary">No payout batches yet.</p>
                <p className="text-xs text-fg-tertiary mt-1">
                  Batches will appear here after you process payouts.
                </p>
              </div>
            </CardBody>
          </Card>
        ) : (
          <div className="space-y-3">
            {recent_batches.map((batch) => {
              const isExpanded = expandedBatches.has(batch.id);
              return (
                <Card key={batch.id}>
                  <CardBody className="p-0">
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedBatches((prev) => {
                          const next = new Set(prev);
                          next.has(batch.id) ? next.delete(batch.id) : next.add(batch.id);
                          return next;
                        });
                      }}
                      className="flex w-full items-center justify-between p-4 text-left hover:bg-surface-secondary/50 transition-colors rounded-lg"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant={BATCH_STATUS_VARIANTS[batch.status] || 'default'} size="sm">
                            {batch.status}
                          </Badge>
                          <span className="text-sm font-medium text-fg">
                            {formatCurrency(batch.total_amount_cents)}
                          </span>
                          <span className="text-xs text-fg-tertiary">
                            {batch.affiliate_count} affiliate{batch.affiliate_count !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-fg-tertiary">
                          <span>Triggered: {batch.triggered_by}</span>
                          {batch.payout_batch_id && (
                            <span className="font-mono">{batch.payout_batch_id}</span>
                          )}
                          <span>{formatDateTime(batch.created_at)}</span>
                        </div>
                        {batch.error_message && (
                          <p className="text-xs text-danger-600 mt-1">{batch.error_message}</p>
                        )}
                      </div>
                      <ChevronDown
                        size={16}
                        strokeWidth={1.5}
                        className={cn(
                          'text-fg-tertiary shrink-0 transition-transform duration-200',
                          isExpanded && 'rotate-180',
                        )}
                      />
                    </button>
                    {isExpanded && batch.items?.length > 0 && (
                      <div className="border-t border-edge-strong">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-edge text-left text-xs font-medium text-fg-tertiary uppercase tracking-wider">
                              <th className="px-4 py-2">Affiliate</th>
                              <th className="px-4 py-2">PayPal Email</th>
                              <th className="px-4 py-2 text-right">Commissions</th>
                              <th className="px-4 py-2 text-right">Amount</th>
                              <th className="px-4 py-2">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {batch.items.map((item) => (
                              <tr key={item.id} className="border-b border-edge last:border-0">
                                <td className="px-4 py-2.5">
                                  <Link href={admin_affiliate_path(item.affiliate_id)} className="block hover:underline">
                                    <div className="font-medium text-fg">{item.affiliate_name}</div>
                                    <div className="text-xs text-fg-tertiary">{item.affiliate_email}</div>
                                  </Link>
                                </td>
                                <td className="px-4 py-2.5 text-fg-secondary">
                                  {item.paypal_email || <span className="text-fg-tertiary">—</span>}
                                </td>
                                <td className="px-4 py-2.5 text-right text-fg-secondary">
                                  {item.commission_count}
                                </td>
                                <td className="px-4 py-2.5 text-right font-medium text-fg">
                                  {formatCurrency(item.amount_cents)}
                                </td>
                                <td className="px-4 py-2.5">
                                  <Badge variant={BATCH_STATUS_VARIANTS[item.status] || 'default'} size="sm">
                                    {item.status}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              );
            })}
          </div>
        )}
      </TabPanel>

      <ConfirmDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleBulkMarkPaid}
        title="Mark Commissions as Paid"
        message={`Are you sure you want to mark all payable commissions for ${selectedCount} affiliate${selectedCount !== 1 ? 's' : ''} as paid? Total payout: ${formatCurrency(selectedTotal)}`}
        confirmLabel="Mark as Paid"
        loading={bulkLoading}
      />

      <Modal
        open={showPaypalConfirm}
        onClose={() => {
          setShowPaypalConfirm(false);
          setSkipMinimum(false);
        }}
        size="sm"
      >
        <ModalTitle>Pay via PayPal</ModalTitle>
        <ModalBody>
          <p className="text-sm text-fg-secondary">
            Send PayPal payouts to {selectedCount} affiliate{selectedCount !== 1 ? 's' : ''}?
            Total: {formatCurrency(selectedTotal)}. Commissions will be marked as paid.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setShowPaypalConfirm(false);
              setSkipMinimum(false);
            }}
            disabled={paypalLoading}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handlePaypalPayout} loading={paypalLoading}>
            Send PayPal Payout
          </Button>
        </ModalFooter>
      </Modal>
    </AdminLayout>
  );
}
